import * as React from 'react'
import * as PropTypes from 'prop-types'
import { connect } from 'react-redux'
import * as R from 'ramda'

import { denormalize, Schema } from 'normalizr'
import {
  resolveSchema,
  transformNumericKeyedObjectToArray,
  getAxiosInstance,
  isObject,
  getRequestMiddleware,
} from './utils'

export { setAxiosOptions } from './utils'
import type { ConnectApiState } from './store'
export { enhanceReducer, resetStore } from './store'

export const connectApiObject = PropTypes.shape({
  data: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array,
    PropTypes.bool,
    PropTypes.number,
    PropTypes.string,
  ]),
  loading: PropTypes.bool.isRequired,
  error: PropTypes.object,
})

interface Config {
  name: string
  url: string
  schema: Schema
  skip?: boolean
  method?: 'get' | 'post' | 'put' | 'patch' | 'delete'
  data?: object
  update?: Function
  delay?: number
  onLoad?: Function
}

function getOptions(config: Config, props): Config {
  // @ts-ignore
  return R.pipe(
    R.pick(['name', 'url', 'schema', 'skip', 'method', 'data', 'delay']),
    R.assoc('method', R.defaultTo('get', config.method)),
    // @ts-ignore
    R.over(R.lensProp('schema'), resolveSchema),
    // @ts-ignore
    R.map(R.ifElse(R.is(Function), (fn) => fn(props), R.identity)),
    R.mergeRight(R.pick(['update', 'onLoad'], config)),
    R.over(R.lensProp('onLoad'), R.defaultTo(R.identity))
    // @ts-ignore
  )(config)
}

const createMapState = (config: Config) => (state: ConnectApiState, ownProps) => {
  const options = getOptions(config, ownProps)
  const result = R.pipe(R.path(['api', '__result', `${options.url}:${options.method}`]))(state)
  // @ts-ignore
  const entities = R.pipe(R.omit(['__result']), R.map(R.prop('entities')))(state.api)
  try {
    const denormalized = denormalize(result, options.schema, entities)
    if (denormalized !== null) {
      return { denormalized: transformNumericKeyedObjectToArray(denormalized) }
    }

    return { denormalized: null }
  } catch (e) {
    return {}
  }
}

const mapDispatch = (dispatch) => ({
  setEntities: (data, schema, options) =>
    dispatch({ type: 'connectApi:SET_ENTITIES', data, schema, options }),
})

const createWithRedux = (config) => connect(createMapState(config), mapDispatch)

const removeInternalProps = (props) => R.omit(['setEntities', 'denormalized'], props)

type Data = object | Array<any>

interface Props {
  denormalized?: Data
  setEntities: Function
}

interface State {
  data: Data | null
  loading: boolean
  error: object | null
}

interface CallApiOptions {
  data?: object
  update?: Function
  silentError?: boolean
  delay?: number
  config?: object
}

enum SuspendMode {
  loading = 'loading',
  noData = 'noData',
}

const schemaCache = {}

export const connectApi = (config) => (Component) =>
  createWithRedux(config)(
    class extends React.Component<Props, State> {
      private store: { data: object | null; write: Function; writeEntity: Function }
      private pendingUpdate: Array<boolean> = []
      private pendingUpdatePromise: Promise<any>
      private pendingUpdatePromiseResolve: Function
      private fetchPromise: Promise<any> = Promise.resolve()

      constructor(props) {
        super(props)
        this.state = this.getInitialState()
        this.store = {
          data: null,
          write: (url, data, method) => {
            const canUpdateEntities = Boolean(url)
            const self = this
            if (canUpdateEntities) {
              updateEntities(data, url, method)
            } else {
              updateLocalState(data)
            }

            function updateEntities(data: object | Function, url: string, method: string = 'get') {
              const options = self.getOptions()
              const isSameUrl = options.url === url && options.method === method
              if (typeof data === 'function') {
                // @ts-ignore we could ignore typing for `ramda` because `merge*` actually supports `null` and `undefined`
                data = R.compose(R.mergeDeepRight(self.store.data), data as (arg: any) => any)
              } else if (isSameUrl && isObject(data)) {
                // @ts-ignore we could ignore typing for `ramda` because `merge*` actually supports `null` and `undefined`
                data = R.mergeDeepRight(self.store.data, data)
              }
              self.props.setEntities(data, schemaCache[`${url}:${method}`], {
                url,
                method,
              })
            }

            function updateLocalState(data: object | Function) {
              if (self.getOptions().schema) {
                throw Error(
                  `'update' doesn't support writing null URL with 'schema'. Either use 'schema' with 'update' with URL or do not specify 'schema' and 'update' without URL.`
                )
              }

              const mergeData = (baseData, newData) => {
                if (Array.isArray(newData)) {
                  return newData
                } else if (typeof newData === 'object') {
                  return R.mergeDeepRight(baseData, newData)
                }

                return newData
              }

              if (typeof data === 'function') {
                self._setState((state) => {
                  const defaultToResponseForFirstRender = state.data || self.store.data
                  return {
                    data: mergeData(
                      // @ts-ignore we could ignore typing for `ramda` because `merge*` actually supports `null` and `undefined`
                      R.mergeRight(state.data, self.store.data),
                      data(defaultToResponseForFirstRender)
                    ),
                    loading: false,
                  }
                })
              } else {
                self._setState((state) => {
                  return {
                    // @ts-ignore we could ignore typing for `ramda` because `merge*` actually supports `null` and `undefined`
                    data: mergeData(R.mergeRight(state.data, self.store.data), data),
                    loading: false,
                  }
                })
              }
            }
          },
          writeEntity: (data) => {
            const options = this.getOptions()
            // @ts-ignore we could ignore typing for `ramda` because `merge*` actually supports `null` and `undefined`
            this.props.setEntities(R.mergeRight(this.store.data, data), options.schema, options)
          },
        }
      }

      _setState(state, callback = () => {}) {
        if (this.pendingUpdate.length === 0) {
          this.pendingUpdatePromise = new Promise((resolve) => {
            this.pendingUpdatePromiseResolve = resolve
          })
        }
        if (!this.isUnmounted) {
          this.pendingUpdate.push(true)
          this.setState(state, () => {
            callback()
            this.pendingUpdate.pop()
            if (this.pendingUpdate.length === 0) {
              this.pendingUpdatePromiseResolve()
            }
          })
        } else {
          callback()
        }
      }

      static getDerivedStateFromProps(props) {
        if (props.denormalized === null) {
          return {
            data: null,
          }
        }

        if (props.denormalized) {
          return {
            data: props.denormalized,
          }
        }

        return null
      }

      isUnmounted = false

      getOptions() {
        return getOptions(config, this.props)
      }

      getInitialState() {
        const options = this.getOptions()
        return {
          data: this.props.denormalized || null,
          loading: !options.skip,
          error: null,
        }
      }

      saveEntities(data) {
        const options = this.getOptions()
        this.props.setEntities(data, options.schema, options)
      }

      handleSuccess = (options: Config & CallApiOptions) => (response) => {
        const data = response.data
        const successCallback = () => {
          this.pendingUpdatePromise.then(() =>
            // @ts-ignore I don't have time to fix this yet, but `onLoad` will always be present
            options.onLoad.call(this.getConnectApiObject(), removeInternalProps(this.props))
          )
        }

        if (options.update) {
          this.store.data = data
          options.update(this.store, data)
        } else if (options.schema) {
          this.saveEntities(data)
        } else {
          this._setState(() => ({
            data,
          }))
        }

        this._setState(() => ({
          loading: false,
        }))
        successCallback()

        return data
      }

      handleError = (options: Config & CallApiOptions) => (error) => {
        const errorCallback = () => {
          // @ts-ignore I don't have time to fix this yet, but `onLoad` will always be present
          options.onLoad.call(this.getConnectApiObject(), removeInternalProps(this.props))
        }

        if (error.response) {
          const errorResponse = error.response
          error = errorResponse
        }

        this._setState(
          () => ({
            loading: false,
            error,
          }),
          errorCallback
        )

        if (!options.silentError) {
          throw error
        }
      }

      callApi = (callApiOptions: CallApiOptions) => {
        this._setState(() => ({
          loading: true,
        }))

        const connectApiOptions = this.getOptions()
        const options = R.mergeRight(connectApiOptions, callApiOptions)
        const data = options.data
        const config = options.config

        const requestPromise =
          options.method === 'get'
            ? getRequestMiddleware.callRequest(
                // @ts-ignore I don't have time to fix this yet, but `method` will always be present
                () => getAxiosInstance()[options.method](options.url, data),
                options.url
              )
            : // @ts-ignore I don't have time to fix this yet, but `method` will always be present
              getAxiosInstance()[options.method](options.url, data, config)

        this.fetchPromise.cancel()
        this.fetchPromise = Promise.all([
          requestPromise,
          Promise.delay(options.delay || 0),
        ])
          .then(([response]) => response)
          .then(this.handleSuccess(options))
          .catch(this.handleError(options))
        return this.fetchPromise
      }

      suspend = (mode: SuspendMode = SuspendMode.noData) => {
        if (!SuspendMode[mode]) {
          console.error(`suspend mode should be one of these value ${R.values(SuspendMode)}`)
        }
        if (mode === SuspendMode.loading) {
          if (this.state.loading) {
            throw this.fetchPromise
          }
        }
        if (mode === SuspendMode.noData) {
          if (!this.state.data && !this.state.error) {
            throw this.fetchPromise
          }
        }
      }

      getConnectApiObject() {
        return R.mergeRight(this.state, {
          callApi: this.callApi,
          suspend: this.suspend,
        })
      }

      componentDidMount() {
        const options = this.getOptions()
        schemaCache[`${options.url}:${options.method}`] = options.schema

        if (!options.skip) {
          this.callApi({ silentError: true })
        }
      }

      componentDidUpdate(prevProps) {
        const previousOptions = getOptions(config, prevProps)
        const nextOptions = getOptions(config, this.props)
        if (!R.equals(previousOptions, nextOptions)) {
          !nextOptions.skip && this.callApi({ silentError: true })
        }
      }

      componentWillUnmount() {
        this.isUnmounted = true
      }

      render() {
        const options = this.getOptions()
        const props = {
          [options.name]: this.getConnectApiObject(),
        }
        return <Component {...removeInternalProps(this.props)} {...props} />
      }
    }
  )



// WEBPACK FOOTER //
// ./src/libs/connectApi/index.tsx